import { createElement, type ReactNode, useCallback, useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ApprovedExpenseItem } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import { useCorrectExpenseItemAccountCodeInlinePresenter } from '@webapp/modules/expense/features/correct-expense-item-account-code-inline/presenters/UseCorrectExpenseItemAccountCodeInlinePresenter'
import { type Account, AccountSelect } from '@webapp/platform/finance'
import messages from './messages'

/**
 * Companion hook for ApprovedExpenseItemListSection.
 *
 * Responsibilities:
 * - "Edit approved item" modal open/selected-item state
 * - Inline command-hook integration + injected account cell construction
 * - Modal-success toast (list refresh happens via mutation invalidation,
 *   BHV-014/022)
 */
export const useApprovedExpenseItemListSection = () => {
  const intl = useProcurifyIntl()

  const [selectedItem, setSelectedItem] =
    useState<Nullable<ApprovedExpenseItem>>(null)

  const {
    commands: { correctAccountCode },
    states: { isSaving: isInlineSaving },
  } = useCorrectExpenseItemAccountCodeInlinePresenter()

  const handleEditItem = useCallback((item: ApprovedExpenseItem) => {
    setSelectedItem(item)
  }, [])

  const handleCancel = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const handleSuccess = useCallback(() => {
    setSelectedItem(null)

    toast.success({
      title: intl.formatMessage(messages.editSuccessTitle),
      children: intl.formatMessage(messages.editSuccessMessage),
    })
  }, [intl])

  // Injected into the list feature's account column (ADR-9): a change commits
  // the single-item correction immediately — no confirmation, no modal
  // (BHV-013)
  const renderAccountCell = useCallback(
    (item: ApprovedExpenseItem): ReactNode =>
      createElement(AccountSelect, {
        'aria-label': intl.formatMessage(messages.inlineAccountCellLabel, {
          label: item.label,
        }),
        secondaryCategoryId: item.account.secondaryCategory?.id,
        value: item.account,
        isDisabled: isInlineSaving,
        onChange: (account: Nullable<Account>) => {
          if (!account) return
          correctAccountCode(item.id, account.id)
        },
      }),

    [correctAccountCode, isInlineSaving, intl]
  )

  return {
    states: {
      selectedItem,
      isEditModalOpen: selectedItem !== null,
    },
    methods: {
      onEditItem: handleEditItem,
      onCancel: handleCancel,
      onSuccess: handleSuccess,
      renderAccountCell,
    },
  }
}
